// SD 回放跟不上：建議改錄影畫質（plan SPEC #device-app-sd-slow-tier，Eddy 2026-10-21 選單；firmware#666）。
// 只在獨立攝影機自己 SD 卡的本地回放判斷；<video> 實測卡頓交給 SlowPlaybackJudge，判定跟不上時讀設定投影（錄影畫質列的值與
// can_write，同設定表單）與能力宣告，確認這台有「標準」而且目前是「高」，再看這個瀏覽器有沒有按過 × / 〔不再提示〕，才出提示列。
// 〔改成標準〕= 寫 desired `kinio_recording.quality_tier = 1`、等設備回報；現有錄影照舊用高播，不重載。同 app/home-base useSlowTierHint。

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { KinioError, adopted, converge, getThing, setDesired } from '@kinio/web-sdk';
import { deviceApi } from '../../lib/deviceApi';
import { QUALITY_TIER_KEY } from '../../lib/recordingQuality';
import {
  SLOW_APPLY_WAIT_MS,
  SLOW_DONE_SHOW_MS,
  SlowPlaybackJudge,
  slowHintDismiss,
  slowHintNever,
  slowHintSuppressedFor,
  slowTierEligible,
} from '../../lib/slowTierHint';

export type SlowHintPhase = 'shown' | 'saving' | 'done' | 'sent' | 'failed';
type View = { phase: SlowHintPhase; canWrite: boolean } | null;
type Dev = { apiEndpoint: string; homeId: string; aid: string };

export interface SlowTierHint {
  view: View;
  switchToStandard: () => void;
  dismiss: () => void;
  never: () => void;
}

/**
 * dev = 正在播自己 SD 卡本地回放的獨立攝影機（不是 = null，不判斷）；playKey = 這次播放清單（from / to），
 * 清單接著上一份的結尾（同一段連續錄影的下一份 10 分鐘）只算 3 秒寬限，其他（使用者點 / 拖 / 換來源）全部重算。
 */
export function useSlowTierHint(o: { dev: Dev | null; uid: string; playing: { from: number; to: number } | null; videoRef: RefObject<HTMLVideoElement | null> }): SlowTierHint {
  const { dev, uid, playing, videoRef } = o;
  const judge = useRef(new SlowPlaybackJudge());
  const shown = useRef(false); // 同一次進回放頁最多出一次
  const prev = useRef<{ from: number; to: number } | null>(null);
  const alive = useRef(true);
  const [view, setView] = useState<View>(null);
  const on = !!dev && !!uid && !!playing;
  const devKey = dev ? `${dev.homeId}/${dev.aid}` : '';

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const from = playing?.from ?? null;
  const to = playing?.to ?? null;
  useEffect(() => {
    const now = Date.now();
    const p = prev.current;
    if (from !== null && p && Math.abs(from - p.to) <= 1) judge.current.grace(now);
    else {
      judge.current.restart();
      judge.current.grace(now);
    }
    prev.current = from !== null && to !== null ? { from, to } : null;
  }, [from, to, devKey]);

  const trip = useCallback(async () => {
    if (shown.current || !dev || !uid) return;
    shown.current = true;
    if (slowHintSuppressedFor(uid, dev.aid, Date.now())) return;
    const [settings, th] = await Promise.all([
      deviceApi.settings(dev.apiEndpoint, dev.homeId, dev.aid).catch(() => null),
      getThing(dev.apiEndpoint, dev.homeId, dev.aid).catch(() => null),
    ]);
    if (!alive.current) return;
    const item = settings?.sections.flatMap((s) => s.clusters ?? []).flatMap((c) => c.items ?? []).find((it) => it.key === QUALITY_TIER_KEY) ?? null;
    const caps = (th?.thing as { capabilities?: unknown } | undefined)?.capabilities ?? null;
    const r = slowTierEligible(true, item, caps);
    if (r.eligible) setView({ phase: 'shown', canWrite: r.canWrite });
  }, [dev, uid]);

  useEffect(() => {
    const v = videoRef.current;
    if (!on || !v) return;
    const j = judge.current;
    const eligibleNow = () => !v.paused && v.playbackRate === 1 && document.visibilityState !== 'hidden';
    const onState = (e: Event) => {
      const now = Date.now();
      j.setEligible(eligibleNow());
      if (e.type === 'waiting') j.stall(now);
      else if (e.type === 'seeking') j.grace(now);
      else if (e.type === 'playing' && j.resume(now)) void trip();
      else if (e.type === 'timeupdate' && v.currentTime > 0) j.firstFrame(now);
    };
    const onVis = () => j.setEligible(eligibleNow());
    const types = ['waiting', 'playing', 'pause', 'play', 'seeking', 'ratechange', 'timeupdate'];
    for (const ty of types) v.addEventListener(ty, onState);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      for (const ty of types) v.removeEventListener(ty, onState);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [on, videoRef, trip]);

  useEffect(() => {
    if (view?.phase !== 'done' && view?.phase !== 'sent') return;
    const id = setTimeout(() => setView(null), SLOW_DONE_SHOW_MS);
    return () => clearTimeout(id);
  }, [view?.phase]);

  const switchToStandard = useCallback(() => {
    if (!dev) return;
    const value = 1;
    const state = { kinio_recording: { quality_tier: value } };
    const post = () => setDesired(dev.apiEndpoint, dev.homeId, dev.aid, state, 'vms-web');
    setView((v) => (v ? { ...v, phase: 'saving' } : v));
    void (async () => {
      let phase: SlowHintPhase;
      try {
        await post();
        const target = { cluster: 'kinio_recording', attr: 'quality_tier', value };
        const sh = await converge(dev.apiEndpoint, dev.homeId, dev.aid, target, { repost: post, timeoutMs: SLOW_APPLY_WAIT_MS });
        // 收斂 = desired 清掉；設備實際採用的不是 1（拒收 / 正規化）= 沒改成
        phase = adopted(sh, target) ? 'done' : 'failed';
      } catch (e) {
        phase = e instanceof KinioError && e.code === 'CONVERGE_TIMEOUT' ? 'sent' : 'failed';
      }
      if (alive.current) setView((v) => (v ? { ...v, phase } : v));
    })();
  }, [dev]);

  const dismiss = useCallback(() => {
    if (uid && dev) slowHintDismiss(uid, dev.aid, Date.now());
    setView(null);
  }, [uid, dev]);
  const never = useCallback(() => {
    if (uid && dev) slowHintNever(uid, dev.aid);
    setView(null);
  }, [uid, dev]);

  return { view: on || view?.phase === 'saving' ? view : null, switchToStandard, dismiss, never };
}

/** 影像下方的提示列（不蓋影像、不暫停播放）。 */
export function SlowTierHintBar({ hint }: { hint: SlowTierHint }) {
  const { t } = useTranslation();
  const v = hint.view;
  if (!v) return null;
  const text =
    v.phase === 'done'
      ? t('playback.slow_tier_done')
      : v.phase === 'sent'
        ? t('playback.slow_tier_sent')
        : v.phase === 'failed'
          ? t('playback.slow_tier_failed')
          : v.canWrite
            ? t('playback.slow_tier_hint')
            : t('playback.slow_tier_hint_no_write');
  const ask = v.phase === 'shown' || v.phase === 'saving' || v.phase === 'failed';
  return (
    <p className="pb-notice pb-slow-tier" role="status" data-testid="pb-slow-tier" data-phase={v.phase}>
      {text}{' '}
      {ask && v.canWrite && (
        <button className="btn btn-sm btn-primary" onClick={hint.switchToStandard} disabled={v.phase === 'saving'} aria-busy={v.phase === 'saving'} data-testid="pb-slow-tier-switch">
          {t('playback.slow_tier_switch')}
        </button>
      )}{' '}
      {ask && (
        <button className="btn btn-sm" onClick={hint.never} disabled={v.phase === 'saving'} data-testid="pb-slow-tier-never">
          {t('playback.slow_tier_never')}
        </button>
      )}{' '}
      {v.phase !== 'saving' && (
        <button className="btn btn-sm btn-ghost" onClick={hint.dismiss} aria-label={t('playback.slow_tier_close')} data-testid="pb-slow-tier-close">
          ✕
        </button>
      )}
    </p>
  );
}
